import React, { useState } from "react";
import {
  Box,
  Container,
  Heading,
  Text,
  VStack,
  HStack,
  Button,
  Icon,
  Badge,
  SimpleGrid,
  Flex,
  Progress,
  List,
  ListItem,
  ListIcon,
  Image,
  AspectRatio,
} from "@chakra-ui/react";
import {
  FaCheckCircle,
  FaArrowRight,
  FaBatteryFull,
  FaSolarPanel,
  FaShieldAlt,
  FaDollarSign,
  FaStar,
  FaPhone,
  FaExclamationTriangle,
  FaMapMarkerAlt,
  FaHome,
  FaLeaf,
  FaBolt,
  FaTimesCircle,
} from "react-icons/fa";
import { Link as RouterLink } from "react-router-dom";
import { NavbarWithCallToAction } from "../../../components/chakra/NavbarWithCallToAction/NavbarWithCallToAction";
import { SolarFooter } from "../../../components/chakra/SolarFooter/SolarFooter";
import { SEOHead } from "../../../components/SEOHead";

const SOLAR_GOLD = "#C69214";
const SOLAR_GOLD_HOVER = "#a07e10";
const NAVY = "#141C49";

type QuizAnswer = string | null;

interface QuizOption {
  label: string;
  icon: React.ElementType;
  value: string;
}

interface QuizStep {
  question: string;
  subtext?: string;
  options: QuizOption[];
}

const quizSteps: QuizStep[] = [
  {
    question: "Do you already have solar panels installed on your home?",
    options: [
      { label: "Yes — I already have solar", icon: FaSolarPanel, value: "yes-solar" },
      { label: "No — not yet", icon: FaTimesCircle, value: "no-solar" },
    ],
  },
  {
    question: "Do you own your home?",
    options: [
      { label: "Yes — I own my home", icon: FaHome, value: "owner" },
      { label: "No — I rent", icon: FaTimesCircle, value: "renter" },
    ],
  },
  {
    question: "What is your average quarterly electricity bill?",
    options: [
      { label: "Under $200", icon: FaDollarSign, value: "low" },
      { label: "$200 – $600", icon: FaDollarSign, value: "medium" },
      { label: "Over $600", icon: FaDollarSign, value: "high" },
    ],
  },
  {
    question: "Where is your property located?",
    options: [
      { label: "Queensland", icon: FaMapMarkerAlt, value: "qld" },
      { label: "NSW / VIC / SA", icon: FaMapMarkerAlt, value: "other-state" },
      { label: "WA / NT / TAS", icon: FaMapMarkerAlt, value: "other-state-2" },
    ],
  },
];

const QuizCard: React.FC = () => {
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState<QuizAnswer[]>([null, null, null, null]);
  const [completed, setCompleted] = useState(false);

  const progress = (step / quizSteps.length) * 100;
  const current = quizSteps[step];

  const handleAnswer = (value: string) => {
    const updated = [...answers];
    updated[step] = value;
    setAnswers(updated);
    if (step < quizSteps.length - 1) {
      setStep(step + 1);
    } else {
      setCompleted(true);
    }
  };

  if (completed) {
    return (
      <VStack spacing={5} textAlign="center" py={2}>
        <Icon as={FaCheckCircle} boxSize={14} color={SOLAR_GOLD} />
        <Heading size="md" color={NAVY}>
          Great news — your home likely qualifies!
        </Heading>
        <Text color="gray.600" fontSize="sm" maxW="sm">
          Based on your answers, you may be eligible for up to{" "}
          <Text as="span" fontWeight="800" color={SOLAR_GOLD}>
            30% off
          </Text>{" "}
          battery installation. Secure your free assessment before funding runs out.
        </Text>
        <VStack spacing={3} w="full" pt={1}>
          <Button
            as={RouterLink}
            to="/solar/quote"
            size="lg"
            w="full"
            bg={SOLAR_GOLD}
            color="white"
            _hover={{ bg: SOLAR_GOLD_HOVER }}
            rightIcon={<FaArrowRight />}
            fontWeight="700"
            transition="all 0.2s"
          >
            Claim My Free Assessment
          </Button>
          <Button
            as="a"
            href="tel:1300663765"
            size="md"
            w="full"
            bg={NAVY}
            color="white"
            _hover={{ bg: "#0d1235" }}
            leftIcon={<FaPhone />}
            fontWeight="700"
          >
            Call 1300 663 765
          </Button>
        </VStack>
      </VStack>
    );
  }

  return (
    <VStack spacing={4} w="full">
      <Flex w="full" justify="space-between" align="center">
        <Text fontSize="xs" color="gray.500" fontWeight="700" textTransform="uppercase" letterSpacing="wider">
          Question {step + 1} of {quizSteps.length}
        </Text>
        <Text fontSize="xs" color={SOLAR_GOLD} fontWeight="700">
          ~45 seconds to complete
        </Text>
      </Flex>
      <Progress
        value={progress}
        w="full"
        borderRadius="full"
        size="xs"
        colorScheme="yellow"
        bg="gray.100"
      />
      <Heading size="sm" color={NAVY} textAlign="center" pt={1} lineHeight="1.4">
        {current.question}
      </Heading>
      <VStack spacing={2} w="full">
        {current.options.map((opt) => (
          <Button
            key={opt.value}
            onClick={() => handleAnswer(opt.value)}
            variant="outline"
            borderColor="gray.200"
            bg="white"
            color={NAVY}
            fontWeight="600"
            fontSize="sm"
            h="auto"
            py={4}
            px={5}
            borderRadius="xl"
            leftIcon={<Icon as={opt.icon} color={SOLAR_GOLD} />}
            _hover={{
              borderColor: SOLAR_GOLD,
              bg: `${SOLAR_GOLD}10`,
              transform: "translateY(-1px)",
              boxShadow: "md",
            }}
            transition="all 0.2s"
            whiteSpace="normal"
            textAlign="left"
            justifyContent="flex-start"
            w="full"
          >
            {opt.label}
          </Button>
        ))}
      </VStack>
    </VStack>
  );
};

const galleryImages = [
  { src: "/assets/solar-assets/asset%2019.jpeg", alt: "Residential solar installation Queensland" },
  { src: "/assets/solar-assets/asset%2033.jpeg", alt: "CEC accredited installer on rooftop" },
  { src: "/assets/solar-assets/asset%2021.jpeg", alt: "Solar panel system monitoring" },
  { src: "/assets/solar-assets/asset%2018.jpeg", alt: "Solar installation team" },
  { src: "/assets/solar-assets/asset%2038.jpeg", alt: "Family enjoying solar energy savings" },
  { src: "/assets/solar-assets/asset%2020.jpeg", alt: "Commercial solar installation" },
  { src: "/assets/solar-assets/asset%2013.jpeg", alt: "Solar panels on Queensland home" },
  { src: "/assets/solar-assets/asset%2032.jpeg", alt: "One Solar Australia team" },
];

const SolarSavingsEndingSoon: React.FC = () => {
  return (
    <Box minH="100vh" bg="white">
      <SEOHead
        pagePath="/solar/solar-savings-ending-soon"
        fallbackTitle="Solar Savings & Battery Rebates Ending Soon | One Solar Australia"
        fallbackDescription="Battery rebate funding is capped and ending soon. Check if your Queensland home qualifies for up to 30% off installation — free, no-obligation assessment from One Solar Australia."
      />
      <NavbarWithCallToAction />

      {/* ── Trust Badge Bar ── */}
      <Box bg={SOLAR_GOLD} py={3}>
        <Container maxW="container.xl">
          <Flex
            justify="center"
            align="center"
            gap={{ base: 4, md: 10 }}
            flexWrap="wrap"
          >
            <HStack spacing={2}>
              <Icon as={FaStar} color="white" boxSize={4} />
              <Text color="white" fontWeight="700" fontSize="sm">4.7 Google Reviews</Text>
            </HStack>
            <HStack spacing={2}>
              <Icon as={FaCheckCircle} color="white" boxSize={4} />
              <Text color="white" fontWeight="700" fontSize="sm">Qualified, Experienced Installers</Text>
            </HStack>
            <HStack spacing={2}>
              <Icon as={FaShieldAlt} color="white" boxSize={4} />
              <Text color="white" fontWeight="700" fontSize="sm">Battery &amp; Panel Warranty up to 25 Years</Text>
            </HStack>
          </Flex>
        </Container>
      </Box>

      {/* ── Hero + Embedded Quiz ── */}
      <Box
        position="relative"
        color="white"
        py={{ base: 16, md: 24 }}
        backgroundImage="url('/assets/solar-assets/asset%2037.jpeg')"
        backgroundSize="cover"
        backgroundPosition="center"
        backgroundAttachment={{ base: "scroll", md: "fixed" }}
        _before={{
          content: '""',
          position: "absolute",
          top: 0, left: 0, right: 0, bottom: 0,
          bg: "rgba(20, 28, 73, 0.88)",
          zIndex: 0,
        }}
      >
        <Container maxW="container.xl" position="relative" zIndex={1}>
          <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={{ base: 10, lg: 16 }} alignItems="center">
            {/* Left — Headline */}
            <VStack align={{ base: "center", lg: "flex-start" }} spacing={6} textAlign={{ base: "center", lg: "left" }}>
              <HStack spacing={2} flexWrap="wrap" justify={{ base: "center", lg: "flex-start" }}>
                <Icon as={FaExclamationTriangle} color={SOLAR_GOLD} boxSize={5} />
                <Text color={SOLAR_GOLD} fontWeight="800" fontSize="sm" textTransform="uppercase" letterSpacing="wider">
                  Battery Rebates Ending Soon
                </Text>
              </HStack>

              <Heading
                as="h1"
                fontSize={{ base: "3xl", md: "4xl", lg: "5xl" }}
                fontWeight="black"
                lineHeight="1.1"
              >
                Check if your home qualifies for the new battery rebate
              </Heading>

              <Heading size="xl" color={SOLAR_GOLD} fontWeight="800">
                Up to 30% off installation
              </Heading>

              <Text fontSize="md" color="gray.300" lineHeight="1.8" maxW="lg">
                Rebate funding is capped — check now and secure your savings before the program ends.
                Free, no-obligation assessment from our CEC-accredited team.
              </Text>

              <VStack align={{ base: "center", lg: "flex-start" }} spacing={3} pt={2}>
                {[
                  "Qualified, experienced solar installers",
                  "Up to 25-year battery and panel warranty",
                  "4.7★ Google Reviews from real Queensland clients",
                  "Free assessment — no obligation",
                ].map((item) => (
                  <HStack key={item} spacing={3}>
                    <Icon as={FaCheckCircle} color={SOLAR_GOLD} boxSize={4} flexShrink={0} />
                    <Text fontSize="sm" color="gray.200">{item}</Text>
                  </HStack>
                ))}
              </VStack>
            </VStack>

            {/* Right — Quiz Card */}
            <Box
              bg="white"
              borderRadius="2xl"
              p={{ base: 6, md: 8 }}
              boxShadow="2xl"
              border="2px solid"
              borderColor={`${SOLAR_GOLD}40`}
              w="full"
            >
              <VStack spacing={5} mb={5} textAlign="center">
                <Badge
                  bg={SOLAR_GOLD}
                  color="white"
                  px={4} py={1}
                  borderRadius="full"
                  fontSize="xs"
                  textTransform="uppercase"
                  letterSpacing="wider"
                >
                  Check Your Eligibility
                </Badge>
                <Heading size="md" color={NAVY}>
                  Does Your Home Qualify?
                </Heading>
              </VStack>
              <QuizCard />
            </Box>
          </SimpleGrid>
        </Container>
      </Box>

      {/* ── Stats Strip ── */}
      <Box bg={NAVY} py={12}>
        <Container maxW="container.xl">
          <SimpleGrid columns={{ base: 2, md: 4 }} spacing={8}>
            {[
              { value: "30%", label: "Off Installation via Rebate" },
              { value: "$50k+", label: "Avg Savings Over 25 Years" },
              { value: "25yr", label: "Battery & Panel Warranty" },
              { value: "4.7★", label: "Google Reviews" },
            ].map((s) => (
              <VStack key={s.label} spacing={1} textAlign="center">
                <Heading size="2xl" color={SOLAR_GOLD}>
                  {s.value}
                </Heading>
                <Text fontSize="sm" color="gray.400" textAlign="center">
                  {s.label}
                </Text>
              </VStack>
            ))}
          </SimpleGrid>
        </Container>
      </Box>

      {/* ── Real Results from Real Clients ── */}
      <Box bg="gray.50" py={{ base: 16, md: 24 }}>
        <Container maxW="container.xl">
          <VStack spacing={3} mb={12} textAlign="center">
            <Badge
              bg={SOLAR_GOLD}
              color="white"
              px={4} py={2}
              borderRadius="full"
              fontSize="sm"
              textTransform="uppercase"
              letterSpacing="wider"
            >
              Real Results from Real Clients
            </Badge>
            <Heading size="xl" color={NAVY}>
              See What Australians Are Saving
            </Heading>
            <Text fontSize="lg" color="gray.600" maxW="2xl">
              Our team has helped hundreds of Australians dramatically cut their power bills with solar and battery storage.
            </Text>
          </VStack>

          <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={12} alignItems="center">
            {/* Testimonial */}
            <Box
              bg="white"
              borderRadius="2xl"
              p={8}
              boxShadow="xl"
              border="2px solid"
              borderColor={`${SOLAR_GOLD}30`}
              position="relative"
            >
              <Box
                position="absolute"
                top={-4}
                left={8}
                bg={SOLAR_GOLD}
                color="white"
                px={4}
                py={1}
                borderRadius="full"
                fontSize="xs"
                fontWeight="700"
                textTransform="uppercase"
                letterSpacing="wider"
              >
                Featured Client
              </Box>

              <VStack align="flex-start" spacing={5} pt={4}>
                <HStack spacing={1}>
                  {[1, 2, 3, 4, 5].map((i) => (
                    <Icon key={i} as={FaStar} color={SOLAR_GOLD} boxSize={4} />
                  ))}
                </HStack>

                <Text color="gray.700" fontSize="lg" lineHeight="1.9" fontStyle="italic">
                  "Fast install, clear communication and our evening bills dropped immediately.
                  We genuinely couldn't believe the difference — I wish we'd done it sooner."
                </Text>

                <HStack spacing={3}>
                  <Box
                    w={10} h={10}
                    bg={NAVY}
                    borderRadius="full"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    color="white"
                    fontWeight="800"
                    fontSize="md"
                    flexShrink={0}
                  >
                    R
                  </Box>
                  <VStack align="flex-start" spacing={0}>
                    <Text fontWeight="700" color={NAVY} fontSize="sm">Rachel S.</Text>
                    <Text color="gray.500" fontSize="xs">Gold Coast, QLD — February 2025</Text>
                  </VStack>
                </HStack>

                {/* Case Study Stats */}
                <Box w="full" bg="gray.50" borderRadius="xl" p={5} mt={1}>
                  <Text
                    fontSize="xs"
                    fontWeight="700"
                    color={SOLAR_GOLD}
                    textTransform="uppercase"
                    letterSpacing="wider"
                    mb={3}
                  >
                    Case Study Details
                  </Text>
                  <SimpleGrid columns={3} spacing={4} mb={4}>
                    {[
                      { label: "System", value: "10kWh Battery" },
                      { label: "Bill Before", value: "$297/qtr" },
                      { label: "Bill After", value: "$12/qtr" },
                    ].map((d) => (
                      <VStack key={d.label} spacing={1} textAlign="center">
                        <Text fontWeight="800" color={NAVY} fontSize="lg">{d.value}</Text>
                        <Text color="gray.500" fontSize="xs">{d.label}</Text>
                      </VStack>
                    ))}
                  </SimpleGrid>
                  <Flex
                    p={3}
                    bg={NAVY}
                    borderRadius="lg"
                    justify="space-between"
                    align="center"
                  >
                    <Text fontSize="sm" color="white" fontWeight="600">Projected 25-Year Savings</Text>
                    <Text fontSize="lg" fontWeight="900" color={SOLAR_GOLD}>$50,000+</Text>
                  </Flex>
                </Box>
              </VStack>
            </Box>

            {/* Why Add a Battery */}
            <VStack align="flex-start" spacing={6}>
              <Badge
                bg={SOLAR_GOLD}
                color="white"
                px={4} py={2}
                borderRadius="full"
                fontSize="sm"
                textTransform="uppercase"
                letterSpacing="wider"
              >
                Why Add a Battery?
              </Badge>
              <Heading size="xl" color={NAVY} lineHeight="1.2">
                Your Solar Panels Work 9 to 5. Your Battery Works 24/7.
              </Heading>
              <Text color="gray.600" fontSize="md" lineHeight="1.8">
                Without a battery, any solar energy you don't use immediately gets exported back to the
                grid — often at a low feed-in rate. A battery lets you store that energy and use it at
                night, during peak pricing, or when the grid goes down.
              </Text>
              <List spacing={4} pt={2}>
                {[
                  { icon: FaBolt, text: "Use stored clean power at night — no more peak-rate surprises" },
                  { icon: FaShieldAlt, text: "Automatic backup protection during grid outages" },
                  { icon: FaDollarSign, text: "Avoid expensive peak electricity rates every day" },
                  { icon: FaLeaf, text: "Run your home on 100% clean energy, around the clock" },
                  { icon: FaBatteryFull, text: "Earn up to 45c/kWh through Virtual Power Plant programs" },
                ].map((item) => (
                  <ListItem key={item.text} display="flex" alignItems="flex-start" gap={3}>
                    <ListIcon as={item.icon} color={SOLAR_GOLD} mt="3px" flexShrink={0} boxSize={4} />
                    <Text fontSize="sm" color="gray.700" lineHeight="1.7">{item.text}</Text>
                  </ListItem>
                ))}
              </List>
              <HStack spacing={4} pt={2} flexWrap="wrap">
                <Button
                  as={RouterLink}
                  to="/solar/quote"
                  bg={SOLAR_GOLD}
                  color="white"
                  size="lg"
                  rightIcon={<FaArrowRight />}
                  _hover={{ bg: SOLAR_GOLD_HOVER }}
                  transition="all 0.2s"
                >
                  Get My Free Assessment
                </Button>
                <Button
                  as="a"
                  href="tel:1300663765"
                  size="lg"
                  bg={NAVY}
                  color="white"
                  leftIcon={<FaPhone />}
                  _hover={{ bg: "#0d1235" }}
                  transition="all 0.2s"
                >
                  Call 1300 663 765
                </Button>
              </HStack>
            </VStack>
          </SimpleGrid>
        </Container>
      </Box>

      {/* ── Image Gallery ── */}
      <Container maxW="container.xl" py={{ base: 16, md: 20 }}>
        <VStack spacing={3} mb={10} textAlign="center">
          <Badge
            bg={SOLAR_GOLD}
            color="white"
            px={4} py={2}
            borderRadius="full"
            fontSize="sm"
            textTransform="uppercase"
            letterSpacing="wider"
          >
            Our Work
          </Badge>
          <Heading size="xl" color={NAVY}>Installations Across Queensland</Heading>
          <Text color="gray.600" maxW="xl" fontSize="md">
            From residential rooftops to commercial sites — real installs, real savings.
          </Text>
        </VStack>
        <SimpleGrid columns={{ base: 2, md: 4 }} spacing={3}>
          {galleryImages.map((img) => (
            <AspectRatio key={img.src} ratio={1}>
              <Box
                borderRadius="xl"
                overflow="hidden"
                boxShadow="md"
                _hover={{ boxShadow: "xl", transform: "scale(1.02)" }}
                transition="all 0.3s"
              >
                <Image
                  src={img.src}
                  alt={img.alt}
                  w="full"
                  h="full"
                  objectFit="cover"
                />
              </Box>
            </AspectRatio>
          ))}
        </SimpleGrid>
      </Container>

      {/* ── Final CTA ── */}
      <Box
        position="relative"
        py={{ base: 20, md: 28 }}
        backgroundImage="url('/assets/solar-assets/asset%2013.jpeg')"
        backgroundSize="cover"
        backgroundPosition="center"
        backgroundAttachment={{ base: "scroll", md: "fixed" }}
        _before={{
          content: '""',
          position: "absolute",
          top: 0, left: 0, right: 0, bottom: 0,
          bg: "rgba(20, 28, 73, 0.92)",
          zIndex: 0,
        }}
      >
        <Container maxW="container.lg" position="relative" zIndex={1}>
          <VStack spacing={6} textAlign="center" color="white">
            <Icon as={FaExclamationTriangle} color={SOLAR_GOLD} boxSize={10} />
            <Heading
              size="2xl"
              fontWeight="black"
              maxW="3xl"
              lineHeight="1.2"
              textTransform="uppercase"
              letterSpacing="wide"
            >
              See How Much You Can Save With Solar!
            </Heading>
            <Text fontSize="lg" color="gray.300" maxW="2xl" lineHeight="1.8">
              Rebate funding is capped and ending soon. Book your free, no-obligation assessment
              today and lock in your savings before the program closes.
            </Text>
            <HStack spacing={4} pt={4} flexWrap="wrap" justify="center">
              <Button
                as={RouterLink}
                to="/solar/quote"
                size="lg"
                bg={SOLAR_GOLD}
                color="white"
                _hover={{ bg: SOLAR_GOLD_HOVER }}
                rightIcon={<FaArrowRight />}
                px={10}
                fontWeight="700"
                transition="all 0.2s"
              >
                Get Your Free Quote
              </Button>
              <Button
                as="a"
                href="tel:1300663765"
                size="lg"
                variant="outline"
                borderColor="white"
                color="white"
                _hover={{ bg: SOLAR_GOLD, borderColor: SOLAR_GOLD }}
                leftIcon={<FaPhone />}
                px={8}
                fontWeight="700"
                transition="all 0.2s"
              >
                1300 663 765
              </Button>
            </HStack>
            <Text fontSize="xs" color="gray.500" pt={2}>
              Free assessment. No obligation. CEC accredited installers. ACN 642 690 435
            </Text>
          </VStack>
        </Container>
      </Box>

      <SolarFooter />
    </Box>
  );
};

export default SolarSavingsEndingSoon;
